import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, EmptyState, TextField } from '../components/primitives';
import { PersonalTodoRow as PersonalTodoRowView } from '../components/personal-todos/PersonalTodoRow';
import { PersonalTodosFilterMenu } from '../components/personal-todos/PersonalTodosFilterMenu';
import { PersonalTodosSectionHead } from '../components/personal-todos/PersonalTodosSectionHead';
import { PersonalTodosSortMenu } from '../components/personal-todos/PersonalTodosSortMenu';
import { usePersonalTodoEditSession } from '../hooks/usePersonalTodoEditSession';
import { supabase } from '../lib/supabase';
import {
  personalTodosStateToSearchParams,
  type PersonalTodoRow,
  type PersonalTodosListState
} from '../lib/models/personalTodos';
import { usePersonalTodosVM } from '../lib/vm/usePersonalTodosVM';
import './PersonalTodos.css';

function rowProps(
  row: PersonalTodoRow,
  edit: ReturnType<typeof usePersonalTodoEditSession>,
  actions: ReturnType<typeof usePersonalTodosVM>['actions']
) {
  return {
    editSession: edit.viewForRow(row),
    onTitleClick: () => edit.handleTitleClick(row),
    onTitleFocus: edit.setTitleFocus,
    onBodyFocus: edit.setBodyFocus,
    onTitleDraftChange: edit.setTitleDraft,
    onBodyDraftChange: edit.setBodyDraft,
    onSave: () => edit.saveSession(actions.saveContent),
    onOpenNotes: () => edit.startSession(row),
    onCloseNotes: edit.discardSession,
    actions
  };
}

export function PersonalTodos() {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewerId, setViewerId] = useState<string | null>(null);
  const edit = usePersonalTodoEditSession();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setViewerId(data.session?.user.id ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const vm = usePersonalTodosVM({ viewerId, searchParams });
  const { state, derived, actions } = vm;

  const writeUrl = (next: PersonalTodosListState) => {
    const params = personalTodosStateToSearchParams(next);
    const search = params.toString();
    navigate(
      { pathname: '/personal-todos', search: search ? `?${search}` : '' },
      { replace: false }
    );
  };

  const [quickAddValue, setQuickAddValue] = useState('');
  const quickAddEmptyRef = useRef<HTMLInputElement>(null);
  const quickAddMainRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (derived.isEmpty && quickAddEmptyRef.current) {
      quickAddEmptyRef.current.focus();
    }
  }, [derived.isEmpty]);

  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    const el = document.querySelector(`[data-testid="pt-row-${focusId}"]`);
    el?.scrollIntoView({ block: 'center' });
  }, [searchParams, state.loadStatus]);

  const onQuickAddSubmit = async (
    e: FormEvent<HTMLFormElement>,
    target: 'empty' | 'main'
  ) => {
    e.preventDefault();
    const trimmed = quickAddValue.trim();
    if (trimmed.length === 0) return;
    setQuickAddValue('');
    await actions.quickAdd(trimmed);
    const ref = target === 'empty' ? quickAddEmptyRef : quickAddMainRef;
    ref.current?.focus();
  };

  const wrapToggleDone = useCallback(
    async (id: string) => {
      edit.discardSession();
      await actions.toggleDone(id);
    },
    [edit, actions]
  );

  const wrapArchive = useCallback(
    async (id: string) => {
      edit.discardSession();
      await actions.archive(id);
    },
    [edit, actions]
  );

  const rowActions = useMemo(
    () => ({
      ...actions,
      toggleDone: wrapToggleDone,
      archive: wrapArchive
    }),
    [actions, wrapToggleDone, wrapArchive]
  );

  const openRows = derived.filteredOpen;
  const doneRows = derived.filteredDone;
  const archivedRows = derived.filteredArchived;

  const sharedRow = (row: PersonalTodoRow) =>
    rowProps(row, edit, rowActions);

  return (
    <section
      className="pt-page"
      data-testid="personal-todos-page"
      data-status={state.loadStatus}
      data-status-filter={derived.listState.status}
      data-sort={derived.listState.sort}
      data-include-archived={derived.includeArchived ? '1' : '0'}
      data-empty={derived.isEmpty ? '1' : '0'}
      data-filtered-empty={derived.isFilteredEmpty ? '1' : '0'}
      data-edit-session={edit.session ? edit.session.id : ''}
    >
      <header className="pt-header">
        <div className="pt-header-titles">
          <h1 className="pt-h1">Your todos</h1>
          <p className="pt-subtitle">
            Org-private. Doesn&apos;t sync to your team. Lives across orgs.
          </p>
        </div>
        <div className="pt-toolbar" data-testid="pt-toolbar">
          <PersonalTodosFilterMenu
            state={derived.listState}
            onChange={writeUrl}
          />
          <PersonalTodosSortMenu
            sort={derived.listState.sort}
            onChange={(sort) => writeUrl({ ...derived.listState, sort })}
          />
        </div>
      </header>

      {derived.isEmpty ? (
        <Card className="pt-onboarding" data-testid="pt-empty">
          <EmptyState>
            Your todos. Org-private. Doesn&apos;t sync to your team. Lives
            across orgs. Type a quick reminder below to add your first todo.
          </EmptyState>
          <form
            className="pt-onboarding-quickadd"
            onSubmit={(e) => {
              void onQuickAddSubmit(e, 'empty');
            }}
          >
            <TextField
              ref={quickAddEmptyRef}
              id="pt-quick-add"
              label="Add a todo"
              hideLabel
              placeholder="+ Add a todo…"
              value={quickAddValue}
              onChange={(e) => setQuickAddValue(e.currentTarget.value)}
              data-testid="pt-quick-add"
              autoFocus
            />
          </form>
        </Card>
      ) : (
        <>
          <form
            className="pt-quickadd"
            onSubmit={(e) => {
              void onQuickAddSubmit(e, 'main');
            }}
          >
            <TextField
              ref={quickAddMainRef}
              id="pt-quick-add"
              label="Add a todo"
              hideLabel
              placeholder="+ Add a todo…"
              value={quickAddValue}
              onChange={(e) => setQuickAddValue(e.currentTarget.value)}
              data-testid="pt-quick-add"
            />
          </form>

          {derived.isFilteredEmpty ? (
            <div className="pt-filtered-empty" data-testid="pt-filtered-empty">
              <EmptyState>No todos match these filters.</EmptyState>
              <button
                type="button"
                className="pt-filtered-clear"
                data-testid="pt-filtered-clear"
                onClick={() =>
                  writeUrl({
                    ...derived.listState,
                    status: 'open',
                    priority: 'all',
                    due: 'all',
                    includeArchived: false
                  })
                }
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {derived.showOpenSection ? (
                <section className="pt-section" data-testid="pt-section-open">
                  {openRows.length > 0 ? (
                    <>
                      <PersonalTodosSectionHead
                        title="Open"
                        count={openRows.length}
                        variant="accent"
                      />
                      <div className="pt-list">
                        {openRows.map((row, idx) => (
                          <PersonalTodoRowView
                            key={row.id}
                            row={row}
                            done={false}
                            archived={false}
                            showReorder
                            sortLocked={derived.sortLocked}
                            isFirst={idx === 0}
                            isLast={idx === openRows.length - 1}
                            {...sharedRow(row)}
                          />
                        ))}
                      </div>
                    </>
                  ) : derived.hasAnyTodos ? (
                    <EmptyState data-testid="pt-open-empty">
                      All caught up.
                    </EmptyState>
                  ) : null}
                </section>
              ) : null}

              {derived.showDoneSection && doneRows.length > 0 ? (
                <section
                  className="pt-section"
                  data-testid="pt-section-recently-done"
                >
                  <PersonalTodosSectionHead
                    title={derived.doneSectionLabel}
                    count={doneRows.length}
                    variant="muted"
                  />
                  <div className="pt-list">
                    {doneRows.map((row) => (
                      <PersonalTodoRowView
                        key={row.id}
                        row={row}
                        done
                        archived={false}
                        showReorder={false}
                        sortLocked={derived.sortLocked}
                        isFirst={false}
                        isLast={false}
                        {...sharedRow(row)}
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {derived.includeArchived ? (
                <section
                  className="pt-section"
                  data-testid="pt-section-archived"
                >
                  <PersonalTodosSectionHead
                    title="Archived"
                    count={archivedRows.length}
                    variant="muted"
                    data-testid="pt-archived-head"
                  />
                  {archivedRows.length === 0 ? (
                    <EmptyState data-testid="pt-archived-empty">
                      No archived todos.
                    </EmptyState>
                  ) : (
                    <div className="pt-list">
                      {archivedRows.map((row) => (
                        <PersonalTodoRowView
                          key={row.id}
                          row={row}
                          done
                          archived
                          showReorder={false}
                          sortLocked={derived.sortLocked}
                          isFirst={false}
                          isLast={false}
                          {...sharedRow(row)}
                        />
                      ))}
                    </div>
                  )}
                </section>
              ) : null}
            </>
          )}
        </>
      )}
    </section>
  );
}
